import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ArrowLeft, ArrowRight, Calendar, CalendarCheck, CheckCircle, Download,
  FileSignature, Plus, Send, Trash2, Type, UploadCloud, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useFacility } from '../context/FacilityContext';
import signableDocsService from '../services/signableDocsService';
import { formatDateTime } from '../utils/datetime';
import { apiErrorText } from '../utils/apiError';

/**
 * Send any PDF for eSign — placed-field two-party flow (owner, 2026-08-14).
 *
 * Upload a PDF/Word doc → name the parties (From = the operator, To = one
 * or more recipients) → PLACE signature boxes and text details directly on
 * the rendered pages → click-to-sign your own box → send. Your signature
 * and the text burn into the PDF at send; each recipient gets their own
 * link and signs at their marked spot; when the last one signs, everyone
 * (From + all To addresses) is emailed the fully signed copy.
 *
 * Pages render via pdfjs (dynamic import so the ESM module never loads in
 * the jsdom smoke suite). The worker is a version-locked copy at
 * public/pdf.worker.min.mjs — re-copy it from
 * node_modules/pdfjs-dist/build/ when upgrading pdfjs-dist.
 */

const STATUS_STYLE = {
  signed: 'bg-green-50 text-green-700 ring-green-200',
  sent: 'bg-blue-50 text-blue-700 ring-blue-200',
  draft: 'bg-slate-50 text-slate-500 ring-slate-200',
  expired: 'bg-amber-50 text-amber-700 ring-amber-200',
  voided: 'bg-slate-50 text-slate-400 ring-slate-200',
};

const SIG_W = 0.3;
const SIG_H = 0.045;
const TEXT_W = 0.3;
const TEXT_H = 0.04;

let fieldSeq = 0;
const nextFieldId = () => `f${Date.now().toString(36)}${(fieldSeq += 1)}`;

/** Render the PDF's pages to canvases and overlay the placed boxes. */
const PlacementPages = ({ pdfUrl, fields, setFields, recipients, fromName, fromSigned, onToggleFromSign }) => {
  const [pages, setPages] = useState([]);      // [{index, w, h}]
  const canvasRefs = useRef({});
  const metasRef = useRef([]);                 // pdfjs page + viewport handles
  const wrapRef = useRef(null);
  const drag = useRef(null);                   // {id, rect, dx, dy}

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const pdfjs = await import('pdfjs-dist');
      pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';
      const docp = await pdfjs.getDocument({ url: pdfUrl }).promise;
      const containerW = Math.min(
        (wrapRef.current && wrapRef.current.clientWidth) || 680, 820);
      const metas = [];
      for (let i = 1; i <= docp.numPages; i += 1) {
        const page = await docp.getPage(i);
        const base = page.getViewport({ scale: 1 });
        const scale = containerW / base.width;
        const vp = page.getViewport({ scale });
        metas.push({ index: i - 1, w: vp.width, h: vp.height, page, vp });
      }
      if (cancelled) return;
      metasRef.current = metas;
      setPages(metas.map(({ index, w, h }) => ({ index, w, h })));
    })().catch(() => toast.error('Could not render the document preview.'));
    return () => { cancelled = true; };
  }, [pdfUrl]);

  // Paint AFTER the canvases are committed to the DOM. A setTimeout here
  // loses the race with React's commit — the refs were still empty and
  // every page stayed a blank 300x150 default canvas (owner report,
  // 2026-08-14). Effects are guaranteed post-commit.
  useEffect(() => {
    if (!pages.length) return undefined;
    let cancelled = false;
    (async () => {
      for (const m of metasRef.current) {
        const canvas = canvasRefs.current[m.index];
        if (!canvas || cancelled) continue;
        canvas.width = m.vp.width;
        canvas.height = m.vp.height;
        try {
          await m.page.render({
            canvasContext: canvas.getContext('2d'),
            viewport: m.vp,
          }).promise;
        } catch { /* superseded render — a newer effect owns the canvas */ }
      }
    })();
    return () => { cancelled = true; };
  }, [pages]);

  const addField = (pageIndex, party, kind, recipient = 0) => {
    setFields((prev) => {
      // Stagger each new box so they never spawn stacked on top of each
      // other (an overlapped input swallows the click-to-sign tap).
      const n = prev.filter((f) => f.page === pageIndex).length;
      const dims = kind === 'signature'
        ? { w: SIG_W, h: SIG_H }
        : kind === 'text'
        ? { w: TEXT_W, h: TEXT_H }
        : { w: 0.16, h: 0.04 };            // date / date_signed
      return [...prev, {
        id: nextFieldId(), party, kind, page: pageIndex,
        x: 0.08 + (n % 2) * 0.42,
        y: Math.min(0.30 + Math.floor(n / 2) * 0.08, 0.9),
        ...dims, recipient, value: '',
      }];
    });
  };
  const removeField = (id) => setFields((prev) => prev.filter((f) => f.id !== id));
  const setValue = (id, value) =>
    setFields((prev) => prev.map((f) => (f.id === id ? { ...f, value } : f)));

  const pointer = (e) => {
    const t = e.touches ? e.touches[0] : e;
    return { px: t.clientX, py: t.clientY };
  };
  const startDrag = (e, f, pageEl) => {
    if (e.target.tagName === 'INPUT' || e.target.closest('[data-nodrag]')) return;
    const rect = pageEl.getBoundingClientRect();
    const { px, py } = pointer(e);
    drag.current = {
      id: f.id, rect, mode: 'move',
      dx: px - (rect.left + f.x * rect.width),
      dy: py - (rect.top + f.y * rect.height),
    };
  };
  const startResize = (e, f, pageEl) => {
    e.stopPropagation();
    drag.current = {
      id: f.id, rect: pageEl.getBoundingClientRect(), mode: 'resize',
    };
  };
  const onMove = (e) => {
    const d = drag.current;
    if (!d) return;
    const { px, py } = pointer(e);
    if (e.touches) e.preventDefault();
    setFields((prev) => prev.map((f) => {
      if (f.id !== d.id) return f;
      if (d.mode === 'resize') {
        const w = Math.min(Math.max((px - d.rect.left) / d.rect.width - f.x, 0.06), 1 - f.x);
        const h = Math.min(Math.max((py - d.rect.top) / d.rect.height - f.y, 0.02), 1 - f.y);
        return { ...f, w, h };
      }
      const x = Math.min(Math.max((px - d.dx - d.rect.left) / d.rect.width, 0), 1 - f.w);
      const y = Math.min(Math.max((py - d.dy - d.rect.top) / d.rect.height, 0), 1 - f.h);
      return { ...f, x, y };
    }));
  };
  const endDrag = () => { drag.current = null; };

  const boxLabel = (f) => {
    if (f.kind === 'text') return null;
    if (f.party === 'from') {
      return fromSigned
        ? <span className="font-serif italic text-blue-800 text-[15px] truncate">{fromName}</span>
        : <span className="text-[11px] text-blue-700">Your signature — click to sign</span>;
    }
    const r = recipients[f.recipient];
    return <span className="text-[11px] text-emerald-700 truncate">{(r && r.name) || `Recipient ${f.recipient + 1}`} signs here</span>;
  };

  return (
    <div
      ref={wrapRef}
      onMouseMove={onMove} onMouseUp={endDrag} onMouseLeave={endDrag}
      onTouchMove={onMove} onTouchEnd={endDrag}
    >
      {pages.length === 0 && (
        <p className="text-[13px] text-slate-400 py-8 text-center">Rendering document…</p>
      )}
      {pages.map((p) => (
        <div key={p.index} className="mb-6">
          <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
            <span className="text-[11px] text-slate-400 mr-1">Page {p.index + 1} — add:</span>
            <button type="button" onClick={() => addField(p.index, 'from', 'signature')}
              className="px-2 py-1 rounded-md bg-blue-600 text-white text-[11px] font-medium hover:bg-blue-700">
              Your signature
            </button>
            {recipients.map((r, i) => (
              <button key={i} type="button" onClick={() => addField(p.index, 'to', 'signature', i)}
                className="px-2 py-1 rounded-md bg-emerald-600 text-white text-[11px] font-medium hover:bg-emerald-700">
                {(r.name || `Recipient ${i + 1}`).split(' ')[0]} signature
              </button>
            ))}
            <button type="button" onClick={() => addField(p.index, 'from', 'text')}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-slate-600 text-white text-[11px] font-medium hover:bg-slate-700">
              <Type className="h-3 w-3" /> Text
            </button>
            <button type="button" onClick={() => addField(p.index, 'from', 'date')}
              title="Pick any date"
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-slate-600 text-white text-[11px] font-medium hover:bg-slate-700">
              <Calendar className="h-3 w-3" /> Date
            </button>
            <button type="button" onClick={() => addField(p.index, 'from', 'date_signed')}
              title="Automatically stamps today's date when you send"
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-slate-600 text-white text-[11px] font-medium hover:bg-slate-700">
              <CalendarCheck className="h-3 w-3" /> Today
            </button>
          </div>
          <div className="relative inline-block border border-slate-200 rounded-md overflow-hidden select-none"
            style={{ width: p.w, maxWidth: '100%' }}>
            <canvas ref={(el) => { canvasRefs.current[p.index] = el; }} style={{ display: 'block', maxWidth: '100%' }} />
            {fields.filter((f) => f.page === p.index).map((f) => (
              <div
                key={f.id}
                onMouseDown={(e) => startDrag(e, f, e.currentTarget.parentElement)}
                onTouchStart={(e) => startDrag(e, f, e.currentTarget.parentElement)}
                onClick={() => { if (f.party === 'from' && f.kind === 'signature') onToggleFromSign(); }}
                className={`absolute flex items-center px-1.5 rounded-sm border cursor-move ${
                  f.kind === 'signature'
                    ? (f.party === 'from'
                        ? 'bg-blue-50/90 border-blue-400'
                        : 'bg-emerald-50/90 border-emerald-400')
                    : 'bg-white/85 border-slate-400'
                }`}
                style={{
                  left: `${f.x * 100}%`, top: `${f.y * 100}%`,
                  width: `${f.w * 100}%`, height: `${f.h * 100}%`,
                }}
              >
                {f.kind === 'text' ? (
                  <input
                    value={f.value || ''}
                    onChange={(e) => setValue(f.id, e.target.value)}
                    placeholder="Type details…"
                    className="w-full bg-transparent text-[12px] text-slate-900 focus:outline-none"
                  />
                ) : f.kind === 'date' ? (
                  <input
                    type="date"
                    value={f.value || ''}
                    onChange={(e) => setValue(f.id, e.target.value)}
                    className="w-full bg-transparent text-[12px] text-slate-900 focus:outline-none"
                  />
                ) : f.kind === 'date_signed' ? (
                  <span className="text-[11px] text-slate-500 truncate">Today's date (auto)</span>
                ) : boxLabel(f)}
                <button type="button" data-nodrag
                  onClick={(e) => { e.stopPropagation(); removeField(f.id); }}
                  className="absolute -top-2.5 -right-2.5 bg-white border border-slate-300 rounded-full p-0.5 text-slate-400 hover:text-rose-600">
                  <X className="h-3 w-3" />
                </button>
                {(f.kind === 'text' || f.kind === 'date') && (
                  <div data-nodrag
                    onMouseDown={(e) => startResize(e, f, e.currentTarget.parentElement.parentElement)}
                    onTouchStart={(e) => startResize(e, f, e.currentTarget.parentElement.parentElement)}
                    title="Drag to resize"
                    className="absolute -bottom-1.5 -right-1.5 w-3.5 h-3.5 bg-white border border-slate-400 rounded-sm cursor-nwse-resize"
                    style={{ backgroundImage: 'linear-gradient(135deg, transparent 50%, #94a3b8 50%)' }}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const UploadFlow = ({ facilityId, onSent }) => {
  const { user } = useAuth();
  const [step, setStep] = useState(1);          // 1 upload, 2 parties, 3 place
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [doc, setDoc] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [fromParty, setFromParty] = useState({ name: '', email: '' });
  const [recipients, setRecipients] = useState([{ name: '', email: '' }]);
  const [fields, setFields] = useState([]);
  const [fromSigned, setFromSigned] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    setFromParty((p) => ({
      name: p.name || user?.full_name || '',
      email: p.email || user?.email || '',
    }));
  }, [user]);

  const reset = () => {
    setStep(1); setFile(null); setTitle(''); setDoc(null); setPdfUrl(null);
    setRecipients([{ name: '', email: '' }]); setFields([]);
    setFromSigned(false); setError(null); setSent(false); setEmailFailed([]);
  };

  const upload = async () => {
    if (!file) return;
    setBusy(true); setError(null);
    try {
      const { data } = await signableDocsService.upload(facilityId, file, title.trim());
      setDoc(data);
      const res = await signableDocsService.pdf(facilityId, data._id);
      setPdfUrl(URL.createObjectURL(res.data));
      setStep(2);
    } catch (e) {
      setError(apiErrorText(e, 'Could not process the file.'));
    } finally { setBusy(false); }
  };

  const validParties = fromParty.name.trim() && fromParty.email.trim()
    && recipients.every((r) => r.name.trim() && r.email.trim());

  // Addresses whose sign-link email the provider did NOT accept — the
  // backend reports them (email_delivery) instead of silently marking the
  // doc sent (incident 2026-09-01). Shown on the sent screen so staff act.
  const [emailFailed, setEmailFailed] = useState([]);

  const send = async () => {
    const hasFromField = fields.some((f) => f.party === 'from' && f.kind === 'signature');
    if (hasFromField && !fromSigned) {
      setError('Click your signature box to sign before sending.');
      return;
    }
    setBusy(true); setError(null);
    try {
      await signableDocsService.saveFields(facilityId, doc._id, {
        fields: fields.map(({ id, party, kind, page, x, y, w, h, recipient, value }) => ({
          id, party, kind, page, x, y, w, h, recipient, value: value || null,
        })),
        from_name: fromParty.name.trim(),
        from_email: fromParty.email.trim(),
      });
      const res = await signableDocsService.sendMulti(facilityId, doc._id,
        recipients.map((r) => ({ name: r.name.trim(), email: r.email.trim() })));
      setEmailFailed(res?.data?.email_delivery?.failed || []);
      setSent(true);
      if (onSent) onSent();
    } catch (e) {
      setError(apiErrorText(e, 'Could not send for signature.'));
    } finally { setBusy(false); }
  };

  if (sent) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <CheckCircle className="w-12 h-12 text-emerald-500 mb-3" />
        <p className="text-[15px] font-medium text-slate-700">Sent for signature</p>
        <p className="text-[13px] text-slate-500 mt-1 max-w-md">
          {recipients.map((r) => `${r.name} (${r.email})`).join(', ')} will each
          receive a link to sign at their marked spot. Once everyone has signed,
          the completed copy is emailed to {fromParty.email} and all signers.
        </p>
        {emailFailed.length > 0 && (
          <div className="mt-4 max-w-md px-4 py-3 rounded-lg bg-amber-50 border border-amber-200 text-[13px] text-amber-800 text-left">
            The sign-link email to <strong>{emailFailed.join(', ')}</strong> was
            not accepted by the email service — that recipient has no signing
            link yet. Check the address and send the document again, or check
            the facility&rsquo;s email settings under Settings.
          </div>
        )}
        <button onClick={reset} className="mt-5 text-[13px] text-blue-600 hover:underline">
          Send another document
        </button>
      </div>
    );
  }

  const inputCls = 'w-full text-[13px] border border-slate-200 rounded-lg px-3 py-2';

  return (
    <div>
      {error && <div className="mb-4 px-3 py-2 rounded-lg bg-red-50 text-red-600 text-[13px]">{error}</div>}

      {step === 1 && (
        <div className="max-w-md">
          <p className="text-[12px] text-slate-400 mb-4">
            Upload the document to send. PDF is used as-is; Word (.docx) is
            converted to PDF automatically.
          </p>
          <label className="block">
            <div className="flex flex-col items-center justify-center gap-2 px-6 py-10 rounded-xl border-2 border-dashed border-slate-200 hover:border-blue-300 cursor-pointer text-center transition-colors">
              <UploadCloud className="w-8 h-8 text-slate-300" />
              {file ? (
                <span className="text-[13px] font-medium text-slate-700">{file.name}</span>
              ) : (
                <span className="text-[13px] text-slate-500">Click to choose a PDF or Word file</span>
              )}
              <span className="text-[11px] text-slate-400">.pdf or .docx — 20 MB max</span>
            </div>
            <input
              type="file"
              accept=".pdf,.docx,.doc,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
          </label>
          <label className="block text-[11px] font-medium text-slate-500 mb-1 mt-4">
            Document name (optional — shown in the signature email)
          </label>
          <input
            value={title} onChange={(e) => setTitle(e.target.value)}
            className={inputCls}
            placeholder={file?.name || 'e.g. Parking addendum'}
          />
          <div className="mt-6 flex justify-end">
            <button onClick={upload} disabled={busy || !file}
              className="flex items-center gap-1.5 text-[13px] px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
              {busy ? 'Processing…' : 'Continue'} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="max-w-md">
          <p className="text-[13px] text-slate-600 mb-4">
            Who is signing? You sign as the sender; each recipient gets their
            own signing link by email.
          </p>
          <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">From (you)</p>
          <div className="grid grid-cols-2 gap-2 mb-4">
            <input value={fromParty.name} placeholder="Your name"
              onChange={(e) => setFromParty((p) => ({ ...p, name: e.target.value }))} className={inputCls} />
            <input value={fromParty.email} type="email" placeholder="Your email"
              onChange={(e) => setFromParty((p) => ({ ...p, email: e.target.value }))} className={inputCls} />
          </div>
          <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">To (signers)</p>
          {recipients.map((r, i) => (
            <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 mb-2">
              <input value={r.name} placeholder="Recipient name"
                onChange={(e) => setRecipients((prev) => prev.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))}
                className={inputCls} />
              <input value={r.email} type="email" placeholder="Recipient email"
                onChange={(e) => setRecipients((prev) => prev.map((x, j) => (j === i ? { ...x, email: e.target.value } : x)))}
                className={inputCls} />
              <button type="button" disabled={recipients.length === 1}
                onClick={() => setRecipients((prev) => prev.filter((_, j) => j !== i))}
                className="px-2 text-slate-400 hover:text-rose-600 disabled:opacity-30">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
          {recipients.length < 5 && (
            <button type="button"
              onClick={() => setRecipients((prev) => [...prev, { name: '', email: '' }])}
              className="inline-flex items-center gap-1 text-[12px] text-blue-600 hover:underline mb-4">
              <Plus className="h-3.5 w-3.5" /> Add another signer
            </button>
          )}
          <div className="flex justify-between mt-4">
            <button onClick={reset} className="flex items-center gap-1.5 text-[13px] text-slate-500 hover:underline">
              <ArrowLeft className="w-4 h-4" /> Different file
            </button>
            <button onClick={() => setStep(3)} disabled={!validParties}
              className="flex items-center gap-1.5 text-[13px] px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
              Place signatures <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div>
          <p className="text-[13px] text-slate-600 mb-1">
            Drop signature boxes and details onto the document, drag them into
            position, then <strong>click your own box to sign it</strong>.
          </p>
          <p className="text-[11px] text-slate-400 mb-4">
            Your signature and any text burn into the document when you send.
            Each recipient signs at their marked spot from their email link.
          </p>
          <PlacementPages
            pdfUrl={pdfUrl}
            fields={fields}
            setFields={setFields}
            recipients={recipients}
            fromName={fromParty.name}
            fromSigned={fromSigned}
            onToggleFromSign={() => setFromSigned((v) => !v)}
          />
          <div className="mt-2 flex justify-between items-center">
            <button onClick={() => setStep(2)} className="flex items-center gap-1.5 text-[13px] text-slate-500 hover:underline">
              <ArrowLeft className="w-4 h-4" /> Back to signers
            </button>
            <button onClick={send} disabled={busy}
              className="flex items-center gap-1.5 text-[13px] px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">
              <Send className="w-4 h-4" /> {busy ? 'Sending…' : `Sign & send to ${recipients.length} signer${recipients.length > 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// What the email provider last reported about EACH recipient's copy of the
// sign-link email (delivered / seen / bounced), stamped by the delivery
// webhook. A signature outranks any email state.
const DELIVERY = {
  signed: { label: 'Signed', cls: 'bg-emerald-50 text-emerald-700 ring-emerald-200' },
  clicked: { label: 'Seen', cls: 'bg-sky-50 text-sky-700 ring-sky-200' },
  opened: { label: 'Seen', cls: 'bg-sky-50 text-sky-700 ring-sky-200' },
  delivered: { label: 'Delivered', cls: 'bg-emerald-50 text-emerald-700 ring-emerald-200' },
  bounced: { label: 'Bounced', cls: 'bg-rose-50 text-rose-700 ring-rose-200' },
  complained: { label: 'Reported as spam', cls: 'bg-rose-50 text-rose-700 ring-rose-200' },
  failed: { label: 'Email failed', cls: 'bg-rose-50 text-rose-700 ring-rose-200' },
  sent: { label: 'Sent', cls: 'bg-slate-50 text-slate-500 ring-slate-200' },
};
const deliveryOf = (r) => (r.signed_at ? 'signed' : (r.email_status || null));
const recipientPills = (d) => {
  const recips = d.recipients && d.recipients.length
    ? d.recipients
    : (d.signing_email_status
      ? [{ name: d.recipient_name, email_status: d.signing_email_status, signed_at: d.signed_at }]
      : []);
  return recips
    .map((r, i) => ({ key: i, name: r.name, state: deliveryOf(r) }))
    .filter((p) => p.state && DELIVERY[p.state]);
};

const SendEsign = () => {
  const { facilityId, currentFacility, loading: facilityLoading } = useFacility();
  const [rows, setRows] = useState([]);
  const [historyLoaded, setHistoryLoaded] = useState(false);

  const loadHistory = useCallback(async () => {
    if (!facilityId) return;
    try {
      const { data } = await signableDocsService.list(facilityId, { doc_type: 'uploaded' });
      setRows(Array.isArray(data) ? data : data?.docs || []);
    } catch { /* history is best-effort */ }
    setHistoryLoaded(true);
  }, [facilityId]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const downloadPdf = async (d, signed) => {
    try {
      const res = await signableDocsService.pdf(facilityId, d._id, signed);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(d.title || 'document').replace(/[^\w.-]+/g, '_')}${signed ? '_signed' : ''}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(apiErrorText(e, 'Could not download the PDF.'));
    }
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete "${d.title || 'this document'}"?${d.status === 'sent' ? ' Its signing link will stop working.' : ''}`)) return;
    try {
      await signableDocsService.remove(facilityId, d._id);
      loadHistory();
    } catch (e) {
      toast.error(apiErrorText(e, 'Could not delete the document.'));
    }
  };

  if (facilityLoading) return null;

  return (
    <div>
      <div className="mb-5">
        <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
          <FileSignature className="h-5 w-5 text-slate-400" />
          Send any PDF for eSign
        </h1>
        <p className="text-[13px] text-slate-400 mt-0.5">
          Upload a document, place the signatures, sign your part, and send
          {currentFacility?.name && <> &middot; {currentFacility.name}</>}
        </p>
      </div>

      <div className="rounded-xl border border-slate-200/80 bg-white p-6 mb-6">
        <UploadFlow facilityId={facilityId} onSent={loadHistory} />
      </div>

      {historyLoaded && rows.length > 0 && (
        <div className="rounded-xl border border-slate-200/80 bg-white overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100">
            <h2 className="text-[14px] font-semibold text-slate-900">Sent documents</h2>
          </div>
          <div className="divide-y divide-slate-50">
            {rows.map((d) => (
              <div key={d._id} className="px-4 py-3 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-medium text-slate-800 truncate">{d.title || d.source_filename || 'Document'}</p>
                  <p className="text-[11px] text-slate-400">
                    {(d.recipients && d.recipients.length
                      ? `To ${d.recipients.map((r) => r.name).join(', ')} · `
                        + `${d.recipients.filter((r) => r.signed_at).length}/${d.recipients.length} signed · `
                      : d.recipient_name
                      ? `To ${d.recipient_name} (${d.recipient_email}) · `
                      : '')}
                    {formatDateTime(d.created_at, currentFacility?.timezone) || ''}
                  </p>
                  {recipientPills(d).length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {recipientPills(d).map((p) => (
                        <span key={p.key}
                          className={`px-1.5 py-0.5 text-[10px] rounded-full ring-1 ring-inset whitespace-nowrap ${DELIVERY[p.state].cls}`}>
                          {p.name ? `${p.name}: ` : ''}{DELIVERY[p.state].label}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full ring-1 ring-inset whitespace-nowrap ${STATUS_STYLE[d.status] || STATUS_STYLE.draft}`}>
                  {d.status}
                </span>
                <button type="button" onClick={() => downloadPdf(d, false)} title="Download PDF"
                  className="p-1.5 rounded-md text-slate-400 hover:text-blue-600 hover:bg-blue-50">
                  <Download className="h-3.5 w-3.5" />
                </button>
                {d.signed_pdf_available && (
                  <button type="button" onClick={() => downloadPdf(d, true)} title="Download signed copy"
                    className="px-2 py-1 rounded-md text-[11px] font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100">
                    Signed PDF
                  </button>
                )}
                <button type="button" onClick={() => remove(d)} title="Delete"
                  className="p-1.5 rounded-md text-slate-400 hover:text-rose-600 hover:bg-rose-50">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SendEsign;
